import { useAsyncFetchFromPublicWabunkaApi } from '@/composables/models/wabunka/useAsyncFetchFromPublicWabunkaApi';
import { buildWabunkaExperienceListViewModel, type WabunkaExperienceListViewModel } from '@/models/wabunka/wabunkaExperience';
import { useCurrency } from '@/wabunka-src/common/composables/general/useCurrency/useCurrency';
import { fromRecommendationListJson } from '@/wabunka-src/features/Experience/composables/query/internal/recommendationResponse';

/**
 * 「You Might Also Like」用 — wabunka-backend 経由で Algolia Recommend `related-products` の
 * 体験本体を Algolia のランキング順で取得する。バックエンドが Redis（TTL 24h）で slug 配列を
 * キャッシュするため、SPA 遷移時もキャッシュが効く。
 *
 * @see docs/Design Doc/自然流入強化-回遊性最適化-レコメンド.md UC3
 */
export const useRelatedExperiencesQuery = async (experienceId: Ref<string>) => {
  // experienceId が空のとき（体験詳細フェッチ失敗で default ViewModel が返るケース等）は
  // `/experiences//recommendations/related` を投げないよう短絡する（JCAT-5042）
  if (!experienceId.value) {
    return { experiences: computed(() => [] as WabunkaExperienceListViewModel) };
  }

  const { selectedCurrency } = useCurrency();
  const path = computed(() => `/experiences/${experienceId.value}/recommendations/related`);
  const query = computed(() => (selectedCurrency.value !== 'JPY' ? { currency: selectedCurrency.value } : {}));

  const { data } = await useAsyncFetchFromPublicWabunkaApi({
    path,
    query,
    options: { server: true, immediate: true, watch: [experienceId, query] },
    converter: fromRecommendationListJson,
    throwOnError: false,
  });

  const experiences = computed(() => buildWabunkaExperienceListViewModel(data.value?.experiences));

  return { experiences };
};
